import type { FocusableElement } from '@react-types/shared';

/* eslint-disable react/jsx-props-no-spreading */
import { Button } from '@coop/components';
import * as React from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { NumericStepper, type UncontrolledNumericStepperHandle } from '../atoms/NumericStepper';

interface QuantityStepperProps {
    size?: 'default' | 'large';
    quantity: number;
    setQuantity: (quantity: number) => void;
    buyButtonRef?: React.RefObject<HTMLButtonElement | null>;
    isBlocked?: (newQuantity: number) => boolean;
    onBlock?: (newQuantity: number, prevQuantity: number) => void;
    withBuyButton?: boolean;
    fullWidth?: boolean;
    onBuyButtonClick?: (e: React.MouseEvent<FocusableElement>) => void;
    onAddClick?: (e: React.MouseEvent<HTMLElement>) => void;
    onRemoveClick?: (e: React.MouseEvent<HTMLElement>) => void;
    buyButtonText?: string;
    buyButtonLabel?: string;
    inputLabel: string;
    addButtonLabel: string;
    removeButtonLabel: string;
    /** Puts instant focus on the buy button when buttons are swapped. This can fix focus but will have side effects that the screen reader order is not correct. */
    autoFocus?: boolean;
}

const QuantityStepper = ({
    size = 'default',
    setQuantity,
    onBlock,
    onAddClick,
    onRemoveClick,
    isBlocked,
    ...props
}: QuantityStepperProps) => {
    const numericStepperRef = useRef<UncontrolledNumericStepperHandle>(null);
    const [optimisticShowBuyButton, setOptimisticShowBuyButton] = useState(
        props.withBuyButton && props.quantity === 0,
    );

    useLayoutEffect(() => {
        // sync internal state with parent, i.e. when add to cart fails
        numericStepperRef.current?.setValue(props.quantity);
    }, [numericStepperRef, props.quantity]);

    useEffect(() => {
        if (props.withBuyButton) {
            setOptimisticShowBuyButton(props.quantity === 0);
        }
    }, [props.quantity, props.withBuyButton]);

    const handleConfirm = useCallback(
        (newQuantity: number, prevQuantity: number) => {
            if (isBlocked?.(newQuantity)) {
                numericStepperRef.current?.revertValue();
                onBlock?.(newQuantity, prevQuantity);
                return;
            }

            setQuantity?.(newQuantity);
            if (props.withBuyButton) {
                setOptimisticShowBuyButton(newQuantity === 0);
            }

            if (props.autoFocus && props.withBuyButton) {
                if (prevQuantity > 0 && newQuantity === 0) {
                    // We need to wait for element to be rendered before we put focus on it
                    requestAnimationFrame(() => {
                        props.buyButtonRef?.current?.focus();
                    });
                } else if (newQuantity > 0 && prevQuantity === 0) {
                    // We need to wait for element to be rendered before we put focus on it
                    requestAnimationFrame(() => {
                        numericStepperRef.current?.focusAddButton();
                    });
                }
            }
        },
        [setQuantity, isBlocked, props.withBuyButton, props.buyButtonRef, onBlock, props.autoFocus],
    );

    return optimisticShowBuyButton ? (
        <Button
            ref={props.buyButtonRef}
            data-testid="add_to_cart_button"
            theme="primary"
            size={size}
            fullWidth={props.fullWidth}
            onClick={(e) => {
                handleConfirm(1, 0);
                props.onBuyButtonClick?.(e);
            }}
            aria-label={props.buyButtonLabel}
            className={!props.fullWidth ? 'u-marginRauto' : ''}
        >
            {props.buyButtonText || 'Köp'}
        </Button>
    ) : (
        <NumericStepper
            ref={numericStepperRef}
            /**
             * If component is used with "Köp" button, then clicking "Köp" should immediately set quanity to 1, without the need to wait for the parent to set quanity, since that can be coupled with backend calls.
             * If component is not using "Köp" button then we need to set the quanity from parent.
             * Default value is only relevant on first render, in subsequent renders useLayoutEffect's numericStepperRef will sync the state with the parent.
             */
            defaultValue={props.quantity || (props.withBuyButton ? 1 : props.quantity)}
            minValue={0}
            incrementBy={1}
            fullWidth={props.fullWidth}
            size={size}
            onConfirm={handleConfirm}
            onAddClick={onAddClick}
            onRemoveClick={onRemoveClick}
            inputLabel={props.inputLabel}
            addButtonLabel={props.addButtonLabel}
            removeButtonLabel={props.removeButtonLabel}
        />
    );
};

export default React.memo(QuantityStepper);
